import { ICommonProductDetails } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { Link } from "@/app/components/lib/navigation/Link";
import { Col, Row } from "@/app/components/lib/my-bootstrap";
import { DiscountTypes } from "@/api/AppApi/services/home/interfaces/IProduct";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import NextImage from "@/app/components/lib/NextImage";
import Price from "@/app/components/lib/Price";
import react from "react";
import { useTranslation } from "@/hooks/localization/useTranslations";
import RateContextMenu from "@/app/components/review-product/product-details-rate/RateContextMenu";

interface Props {
  product: ICommonProductDetails;
  isConfigrablePopup?: boolean;
  link?: react.ReactNode;
  showRate?: boolean;
  getPrise: (priceType: "price" | "price_without_discount" | "discount_amount" | "discount_type" | "discount") => number | DiscountTypes | undefined;
}

const ProductMainInfo: React.FC<Props> = ({
  product,
  isConfigrablePopup = false,
  link,
  showRate,
  getPrise,
}) => {
  const t = useTranslation();
  const getComapny = useGetCompanyQuery();
  const {
    show_discount_badge,
    show_new_badge,
    show_sku,
    show_sold_quantity_badge,
  } = getComapny?.data?.result?.theme?.components?.product ?? {};
  const showNew = show_new_badge && product?.is_new;
  const showDiscount = show_discount_badge && product?.discount_amount > 0;
  const showSoldQuantityBadge =
    !!show_sold_quantity_badge && product.sold_quantity > 0;
  const showBadges = showNew || showDiscount || showSoldQuantityBadge;
  return (
    <div className="main-info">
      <h1
        className="name"
        style={{
          textDecorationLine: isConfigrablePopup ? "underline" : "initial",
          display: "flex",
        }}
      >
        {product.name}
      </h1>
      <div style={{ width: "fit-content" }}>{link}</div>
      {product.brand && (
        <Link
          prefetch={false}
          href={`/brands/${product.brand.slug || product.brand.id}`}
        >
          <Row className="m-0 align-items-center" style={{ gap: 4 }}>
            <Col className="p-0 col-auto">
              <div style={{ width: 24, height: 24 }}>
                <NextImage
                  alt={product.name + " brand"}
                  width={24}
                  height={24}
                  src={product?.brand?.logo_url}
                  objectFit="contain"
                />
              </div>
            </Col>
            <Col className="p-0">
              <div className="brand-logo">{product?.brand?.name}</div>
            </Col>
          </Row>
        </Link>
      )}
      {showRate && (
        <RateContextMenu
          rate={product?.rate}
          ratings={product?.ratings}
          canReview={product?.can_review}
          productId={product.parent_id ?? product.id}
        />
      )}
      {show_sku && product.sku && (
        <div className="sku">{`SKU: ${product?.sku}`}</div>
      )}
      {showBadges && (
        <Row className="m-0 align-items-center" style={{ gap: 4 }}>
          {showNew && (
            <Col className="p-0 col-auto">
              <div className="new">{t.new}</div>
            </Col>
          )}
          {showSoldQuantityBadge && (
            <Col className="p-0 col-auto">
              <div className="new">{`${t.sold} ${product.sold_quantity} ${t.pcs}`}</div>
            </Col>
          )}

          {showDiscount && (() => {
            const discountAmount = getPrise("discount_amount") as number;
            const discountType = getPrise("discount_type") as DiscountTypes | undefined;
            const discountValue = getPrise("discount") as number | undefined;
            
            if (!discountAmount) return null;
            
            return (
              <Col className="col-auto sale" key="discount-badge">
                {discountType === DiscountTypes.rate && discountValue ? (
                  `-${discountValue}%`
                ) : (
                  <Price
                    price={{ value: -discountAmount, className: "" }}
                    roundPrice
                  />
                )}
              </Col>
            );
          })()}
        </Row>
      )}
      {!isConfigrablePopup && product?.description?.length > 0 && (
        <>
          <h2 className="description fw-bold">{t.description}</h2>
          {product?.description?.split("\n").map((d, key) => (
            <p className="description" key={key}>
              {d}
            </p>
          ))}
        </>
      )}
    </div>
  );
};

export default ProductMainInfo;
